import { Badge, Center, Spinner, useTheme } from '@agrotoken-tech/ui';
import { AxiosError } from '@bcba/client-auth';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import {
  Breadcrumbs,
  clientDossierStatusGeneralBadgeColors,
} from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { ExplorerViewCompanyDigitalDossierSerializer } from '@bcba/serializers';
import { ApiExceptionParams, ERROR_CODES } from '@bcba/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';
import { ErrorPage } from '../../pages/ErrorPage';
import { paths } from '../../routes/paths';
import { getSharedDigitalDossier } from '../../services/digital-dossier-access-requests/getSharedDigitalDossier';
import { DigitalDossierLayout } from '../DigitalDossier/shared/DigitalDossierLayout';
import { SharedAffidavitsSection } from './SharedAffidavitsSection';
import { SharedDigitalDossierSection } from './SharedDigitalDossierSection';
import { ReadOnlyContactInfoSection } from './components/ReadOnlyContactInfoSection';
import { ReadOnlyEquityInfoSection } from './components/ReadOnlyEquityInfoSection/ReadOnlyEquityInfoSection';

export const SharedDigitalDossier = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { userHasPendingChanges, setUserHasPendingChanges } = useUserContext();

  const { uuid } = useParams();

  const { data, isPending, isError, error } = useQuery<
    ExplorerViewCompanyDigitalDossierSerializer,
    AxiosError<ApiExceptionParams>
  >({
    queryKey: ['shared-digital-dossier', uuid],
    queryFn: () => getSharedDigitalDossier(uuid ?? ''),
    enabled: Boolean(uuid),
    retry: 1,
  });

  const { dossierStatus } = data ?? {};
  const { digitalDossierStatuses } = dossierStatus ?? {};

  const tabsData = useMemo(() => {
    if (!data) return [];
    const tabs = [
      {
        title: t('common.digitalDossier'),
        content: <SharedDigitalDossierSection data={data} />,
      },
      {
        title: t('common.contactInformation'),
        content: (
          <ReadOnlyContactInfoSection
            contactInformation={data.contactInformation}
          />
        ),
      },
      {
        title: t('common.affidavits'),
        content: (
          <SharedAffidavitsSection
            affidavitsInformation={data.affidavitsInformation}
          />
        ),
      },
      {
        title: t('common.equityInformation'),
        content: (
          <ReadOnlyEquityInfoSection
            equityInformation={data.equityInformation}
          />
        ),
      },
    ];

    return tabs;
  }, [t, data]);

  if (isError) {
    const code = error?.response?.data?.code;
    switch (code) {
      case ERROR_CODES.FORBIDDEN_RESOURCE:
        return <ErrorPage errorCode={ERROR_CODES.FORBIDDEN_RESOURCE} />;
      case ERROR_CODES.RECORD_NOT_FOUND:
        return <ErrorPage />;
      default:
        return (
          <ErrorPage
            errorCode={ERROR_CODES.INTERNAL_SERVER_ERROR}
            mainErrorMessage={t('sharedDigitalDossier.error.default')}
            errorDescription=""
          />
        );
    }
  }

  if (isPending || !data)
    return (
      <Center pt={8}>
        <Spinner />
      </Center>
    );

  const pageTitle = data.generalInformation?.name;

  const lastUpdate = data.lastUpdate;

  return (
    <>
      <Breadcrumbs
        paths={paths}
        onNavigate={(link) => navigate(link)}
        currentPath={location.pathname}
        pageTitle={pageTitle}
        translationPrefix="explorer.navigation.route"
      />
      <DigitalDossierLayout
        theme={theme}
        tabsData={tabsData}
        title={pageTitle}
        badge={
          <>
            {digitalDossierStatuses?.map((status) => (
              <Badge
                key={status}
                colorScheme={clientDossierStatusGeneralBadgeColors[status]}
                mr="2"
              >
                {t(`accessRequests.status.digitalDossierStatus.${status}`)}
              </Badge>
            ))}
            {lastUpdate ? (
              <Badge colorScheme="gray">
                {t('common.lastUpdate').replace(
                  '{{lastUpdate}}',
                  formatDateToShortFormat(unixTimestampToDate(lastUpdate)),
                )}
              </Badge>
            ) : null}
          </>
        }
        hasUserPendingChanges={userHasPendingChanges}
        setUserHasPendingChanges={setUserHasPendingChanges}
      />
    </>
  );
};
